import React from 'react';
import { Loader2, CheckCircle, AlertCircle, ArrowRight, Check, PartyPopper, RefreshCw, ExternalLink, AlertTriangle } from 'lucide-react';
import { formatEther } from 'viem';
import { colors, spacing, typography, borders, shadows, transitions } from '../../../../tokens';
import { Card } from '../../../../atoms';
import { formatTokenAmount, formatErrorMessage, getSwapBlockers } from '../utils/formatters';
import type { SwapStep } from '../hooks';

interface SwapFormProps {
  // Form state
  swapAmount: string;
  swapStep: SwapStep;
  swapAmountBigInt: bigint;
  isMaxHovered: boolean;
  isApproveHovered: boolean;
  isSwapHovered: boolean;
  setSwapAmount: (amount: string) => void;
  setIsMaxHovered: (hovered: boolean) => void;
  setIsApproveHovered: (hovered: boolean) => void;
  setIsSwapHovered: (hovered: boolean) => void;

  // Contract data
  oldTokenBalance: bigint | undefined;
  newTokenBalance: bigint | undefined;
  tokenAllowance: bigint | undefined;
  availablePoolTokens: bigint;
  swapActive: boolean | undefined;

  // Transaction handlers
  onApprove: () => void;
  onSwap: () => void;
  onReset?: () => void;
  onNavigate?: (page: string) => void;

  // Transaction states
  isApprovePending: boolean;
  isApproveLoading: boolean;
  isApproveSuccess: boolean;
  isApproveError: boolean;
  approveError: Error | null;
  isSwapPending: boolean;
  isSwapLoading: boolean;
  isSwapSuccess: boolean;
  isSwapError: boolean;
  swapError: Error | null;
  swapHash: `0x${string}` | undefined;
  lastSwappedAmount?: string;

  // Chain state
  chainId: number | undefined;
  requiredChainId: number;
}

export const SwapForm: React.FC<SwapFormProps> = ({
  swapAmount,
  swapStep,
  swapAmountBigInt,
  isMaxHovered,
  isApproveHovered,
  isSwapHovered,
  setSwapAmount,
  setIsMaxHovered,
  setIsApproveHovered,
  setIsSwapHovered,
  oldTokenBalance,
  newTokenBalance,
  tokenAllowance,
  availablePoolTokens,
  swapActive,
  onApprove,
  onSwap,
  onReset,
  onNavigate,
  isApprovePending,
  isApproveLoading,
  isApproveSuccess,
  isApproveError,
  approveError,
  isSwapPending,
  isSwapLoading,
  isSwapSuccess,
  isSwapError,
  swapError,
  swapHash,
  lastSwappedAmount,
  chainId,
  requiredChainId,
}) => {
  const needsApproval = swapAmountBigInt > 0n && tokenAllowance !== undefined && tokenAllowance < swapAmountBigInt;
  const isApproved = swapAmountBigInt > 0n && tokenAllowance !== undefined && tokenAllowance >= swapAmountBigInt;
  const swapBlockers = getSwapBlockers(swapActive, swapAmount, swapAmountBigInt, oldTokenBalance, availablePoolTokens, isApproved);
  const canSwap = chainId === requiredChainId && swapBlockers.length === 0 && isApproved;

  // Handle reset/swap again
  const handleSwapAgain = () => {
    setSwapAmount('');
    onReset?.();
  };

  // SUCCESS VIEW - Show this instead of the form when swap is successful
  if (isSwapSuccess) {
    const swappedAmountDisplay = lastSwappedAmount || swapAmount || '0';
    const hasMoreTokens = oldTokenBalance !== undefined && oldTokenBalance > 0n;

    return (
      <Card padding="2xl" style={{ border: `2px solid ${colors.brand.cyan.base}` }}>
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            textAlign: 'center',
            padding: spacing.xl,
          }}
        >
          {/* Success Icon */}
          <div
            style={{
              width: 100,
              height: 100,
              borderRadius: '50%',
              backgroundColor: colors.brand.cyan.base + '20',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              marginBottom: spacing.xl,
              animation: 'pulse 2s infinite',
            }}
          >
            <PartyPopper size={48} color={colors.brand.cyan.base} />
          </div>

          {/* Success Title */}
          <h2
            style={{
              fontSize: typography.fontSize['3xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.brand.cyan.base,
              margin: `0 0 ${spacing.md} 0`,
            }}
          >
            Swap Successful!
          </h2>

          {/* Success Description */}
          <p
            style={{
              fontSize: typography.fontSize.lg,
              color: colors.text.secondary.default,
              margin: `0 0 ${spacing.xl} 0`,
              maxWidth: '500px',
            }}
          >
            Your tokens have been successfully swapped. The nFAR tokens are now in your wallet.
          </p>

          {/* Swap Summary */}
          <div
            style={{
              backgroundColor: colors.background.secondary.default,
              borderRadius: borders.component.card.radius,
              padding: spacing.xl,
              marginBottom: spacing.xl,
              width: '100%',
              maxWidth: '400px',
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: spacing.lg,
                paddingBottom: spacing.lg,
                borderBottom: `1px solid ${colors.border.default.default}`,
              }}
            >
              <span style={{ fontSize: typography.fontSize.base, color: colors.text.secondary.default }}>
                You swapped
              </span>
              <span style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: colors.text.primary.default }}>
                {swappedAmountDisplay} cFAR
              </span>
            </div>
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span style={{ fontSize: typography.fontSize.base, color: colors.text.secondary.default }}>
                You received
              </span>
              <span style={{ fontSize: typography.fontSize.xl, fontWeight: typography.fontWeight.bold, color: colors.brand.cyan.base }}>
                {swappedAmountDisplay} nFAR
              </span>
            </div>
          </div>

          {/* Updated Balance */}
          <div
            style={{
              backgroundColor: colors.brand.purple.base + '10',
              borderRadius: borders.component.default.radius,
              padding: spacing.lg,
              marginBottom: spacing.xl,
              width: '100%',
              maxWidth: '400px',
            }}
          >
            <div style={{ fontSize: typography.fontSize.sm, color: colors.text.muted.default, marginBottom: spacing.xs }}>
              Your new nFAR balance
            </div>
            <div style={{ fontSize: typography.fontSize['2xl'], fontWeight: typography.fontWeight.bold, color: colors.brand.purple.base }}>
              {formatTokenAmount(newTokenBalance)} nFAR
            </div>
          </div>

          {/* Transaction Link */}
          {swapHash && (
            <a
              href={`https://bscscan.com/tx/${swapHash}`}
              target="_blank"
              rel="noopener noreferrer"
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacing.xs,
                fontSize: typography.fontSize.sm,
                color: colors.brand.purple.base,
                textDecoration: 'none',
                marginBottom: spacing.xl,
              }}
            >
              <ExternalLink size={14} />
              View transaction on Etherscan
            </a>
          )}

          {/* Remaining Balance Notice */}
          {hasMoreTokens && (
            <div
              style={{
                backgroundColor: colors.brand.yellow.base + '15',
                borderRadius: borders.component.default.radius,
                padding: spacing.lg,
                marginBottom: spacing.xl,
                width: '100%',
                maxWidth: '400px',
                border: `1px solid ${colors.brand.yellow.base}40`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  marginBottom: spacing.xs,
                }}
              >
                <AlertTriangle size={16} color={colors.brand.yellow.base} />
                <span style={{ fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, color: colors.text.primary.default }}>
                  You still have tokens to swap
                </span>
              </div>
              <p style={{ fontSize: typography.fontSize.sm, color: colors.text.secondary.default, margin: 0 }}>
                You have <strong>{formatTokenAmount(oldTokenBalance)} cFAR</strong> remaining in your wallet.
              </p>
            </div>
          )}

          {/* Action Buttons */}
          <div
            style={{
              display: 'flex',
              gap: spacing.md,
              flexWrap: 'wrap',
              justifyContent: 'center',
            }}
          >
            {hasMoreTokens && (
              <button
                onClick={handleSwapAgain}
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: spacing.sm,
                  padding: `${spacing.md} ${spacing.xl}`,
                  backgroundColor: colors.brand.purple.base,
                  color: colors.neutral.white,
                  fontSize: typography.fontSize.base,
                  fontWeight: typography.fontWeight.semibold,
                  borderRadius: borders.component.button.radius,
                  border: 'none',
                  cursor: 'pointer',
                  boxShadow: shadows.interactive.purple,
                  transition: transitions.button,
                }}
              >
                <RefreshCw size={18} />
                Swap More Tokens
              </button>
            )}

            <button
              onClick={() => onNavigate?.('staking')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacing.sm,
                padding: `${spacing.md} ${spacing.xl}`,
                backgroundColor: hasMoreTokens ? colors.background.secondary.default : colors.brand.purple.base,
                color: hasMoreTokens ? colors.text.primary.default : colors.neutral.white,
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.medium,
                borderRadius: borders.component.button.radius,
                border: hasMoreTokens ? `1px solid ${colors.border.default.default}` : 'none',
                cursor: 'pointer',
                transition: transitions.button,
              }}
            >
              Go to Staking
              <ArrowRight size={18} />
            </button>
          </div>
        </div>

        {/* CSS for pulse animation */}
        <style>{`
          @keyframes pulse {
            0%, 100% {
              transform: scale(1);
              opacity: 1;
            }
            50% {
              transform: scale(1.05);
              opacity: 0.9;
            }
          }
        `}</style>
      </Card>
    );
  }

  return (
    <>
      {/* Network Warning - Show if user needs to bridge tokens to BSC */}
      <div
        style={{
          backgroundColor: colors.brand.purple.base + '15',
          borderRadius: borders.component.card.radius,
          padding: spacing.lg,
          marginBottom: spacing.lg,
          border: `1px solid ${colors.brand.purple.base}40`,
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: spacing.md,
          }}
        >
          <AlertTriangle size={24} color={colors.brand.purple.base} style={{ flexShrink: 0, marginTop: 2 }} />
          <div>
            <h4
              style={{
                fontSize: typography.fontSize.base,
                fontWeight: typography.fontWeight.semibold,
                color: colors.text.primary.default,
                margin: `0 0 ${spacing.xs} 0`,
              }}
            >
              Have FAR tokens on Ethereum or Polygon?
            </h4>
            <p
              style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary.default,
                margin: `0 0 ${spacing.sm} 0`,
              }}
            >
              The swap is only available on BSC network. If you have cFAR tokens on other networks,
              please use the Bridge to transfer them to BSC first.
            </p>
            <button
              onClick={() => onNavigate?.('bridge')}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: spacing.xs,
                padding: `${spacing.xs} ${spacing.md}`,
                backgroundColor: colors.brand.purple.base,
                color: colors.neutral.white,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                borderRadius: borders.component.button.radius,
                border: 'none',
                cursor: 'pointer',
              }}
            >
              Go to Bridge
              <ArrowRight size={14} />
            </button>
          </div>
        </div>
      </div>

      {/* Token Balances */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))',
          gap: spacing.lg,
        }}
      >
        {/* OLD Token Balance - Orange if > 0, Green if = 0 */}
        {(() => {
          const hasTokensToSwap = oldTokenBalance !== undefined && oldTokenBalance > 0n;
          const statusColor = hasTokensToSwap ? colors.semantic.warning : colors.semantic.success;

          return (
            <div
              style={{
                backgroundColor: `${statusColor}10`,
                borderRadius: borders.component.card.radius,
                padding: spacing.lg,
                border: `1px solid ${statusColor}40`,
              }}
            >
              <div
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  marginBottom: spacing.sm,
                }}
              >
                <div
                  style={{
                    fontSize: typography.fontSize.sm,
                    color: colors.text.secondary.default,
                  }}
                >
                  Your cFAR Balance
                </div>
                {hasTokensToSwap ? (
                  <AlertTriangle size={18} color={statusColor} />
                ) : (
                  <CheckCircle size={18} color={statusColor} />
                )}
              </div>
              <div
                style={{
                  fontSize: typography.fontSize['4xl'],
                  fontWeight: typography.fontWeight.bold,
                  color: hasTokensToSwap ? statusColor : colors.text.primary.default,
                  marginBottom: spacing.xs,
                }}
              >
                {formatTokenAmount(oldTokenBalance)}
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.sm,
                  color: colors.text.muted.default,
                  marginBottom: spacing.sm,
                }}
              >
                Classic Fartoken
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.xs,
                  color: statusColor,
                  fontWeight: typography.fontWeight.medium,
                  display: 'flex',
                  alignItems: 'center',
                  gap: spacing.xs,
                }}
              >
                {hasTokensToSwap ? (
                  <>
                    <AlertTriangle size={12} />
                    Swap these tokens to get nFAR
                  </>
                ) : (
                  <>
                    <CheckCircle size={12} />
                    All tokens swapped!
                  </>
                )}
              </div>
            </div>
          );
        })()}

        {/* NEW Token Balance */}
        <Card
          style={{
            backgroundColor: `${colors.brand.purple.base}10`,
            border: `1px solid ${colors.brand.purple.base}40`,
          }}
        >
          <div
            style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.sm,
            }}
          >
            Your nFAR Balance
          </div>
          <div
            style={{
              fontSize: typography.fontSize['4xl'],
              fontWeight: typography.fontWeight.bold,
              color: colors.brand.purple.base,
              marginBottom: spacing.xs,
            }}
          >
            {formatTokenAmount(newTokenBalance)}
          </div>
          <div
            style={{
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}
          >
            Super Farnetwork
          </div>
        </Card>
      </div>

      {/* Swap Interface */}
      <Card padding="2xl" style={{ border: `2px solid ${colors.brand.purple.base}` }}>
        <h3
          style={{
            fontSize: typography.fontSize['2xl'],
            fontWeight: typography.fontWeight.semibold,
            color: colors.text.primary.default,
            marginBottom: spacing.lg,
          }}
        >
          Swap Your Tokens
        </h3>

        {/* 2-Step Progress Indicator */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: spacing.md,
            marginBottom: spacing.xl,
            padding: spacing.lg,
            backgroundColor: colors.background.secondary.default,
            borderRadius: borders.component.default.radius,
            border: `1px solid ${colors.border.default.default}`,
          }}
        >
          {/* Step 1: Approve */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              flex: 1,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: !needsApproval
                  ? colors.brand.cyan.base
                  : isApproveLoading || isApprovePending
                  ? colors.brand.yellow.base
                  : colors.neutral.gray[300],
                color:
                  !needsApproval || isApproveLoading || isApprovePending
                    ? colors.neutral.white
                    : colors.text.muted.default,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: typography.fontWeight.bold,
                fontSize: typography.fontSize.sm,
              }}
            >
              {!needsApproval ? (
                <Check size={20} />
              ) : isApproveLoading || isApprovePending ? (
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                '1'
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  color: !needsApproval ? colors.brand.cyan.base : colors.text.primary.default,
                }}
              >
                Approve Token
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.muted.default,
                }}
              >
                {!needsApproval
                  ? 'Completed'
                  : isApproveLoading || isApprovePending
                  ? 'In progress...'
                  : 'Grant permission'}
              </div>
            </div>
          </div>

          {/* Arrow */}
          <ArrowRight size={20} color={colors.text.muted.default} />

          {/* Step 2: Swap */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: spacing.sm,
              flex: 1,
            }}
          >
            <div
              style={{
                width: 32,
                height: 32,
                borderRadius: '50%',
                backgroundColor: isSwapSuccess
                  ? colors.brand.cyan.base
                  : isSwapLoading || isSwapPending
                  ? colors.brand.purple.base
                  : !needsApproval
                  ? colors.neutral.gray[300]
                  : colors.neutral.gray[200],
                color:
                  isSwapSuccess || isSwapLoading || isSwapPending
                    ? colors.neutral.white
                    : colors.text.muted.default,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                fontWeight: typography.fontWeight.bold,
                fontSize: typography.fontSize.sm,
              }}
            >
              {isSwapSuccess ? (
                <Check size={20} />
              ) : isSwapLoading || isSwapPending ? (
                <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
              ) : (
                '2'
              )}
            </div>
            <div style={{ flex: 1 }}>
              <div
                style={{
                  fontSize: typography.fontSize.sm,
                  fontWeight: typography.fontWeight.semibold,
                  color: isSwapSuccess
                    ? colors.brand.cyan.base
                    : !needsApproval
                    ? colors.text.primary.default
                    : colors.text.muted.default,
                }}
              >
                Execute Swap
              </div>
              <div
                style={{
                  fontSize: typography.fontSize.xs,
                  color: colors.text.muted.default,
                }}
              >
                {isSwapSuccess
                  ? 'Completed'
                  : isSwapLoading || isSwapPending
                  ? 'In progress...'
                  : !needsApproval
                  ? 'Ready to swap'
                  : 'Waiting...'}
              </div>
            </div>
          </div>
        </div>

        {/* Approval Success Message */}
        {isApproveSuccess && needsApproval === false && !isSwapSuccess && (
          <div
            style={{
              backgroundColor: colors.brand.cyan.base + '20',
              padding: spacing.lg,
              borderRadius: borders.component.default.radius,
              border: `1px solid ${colors.brand.cyan.base}`,
              marginBottom: spacing.lg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                marginBottom: spacing.sm,
              }}
            >
              <CheckCircle size={20} color={colors.brand.cyan.base} />
              <span
                style={{
                  fontSize: typography.fontSize.base,
                  color: colors.text.primary.default,
                  fontWeight: typography.fontWeight.semibold,
                }}
              >
                Approval Successful!
              </span>
            </div>
            <p
              style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary.default,
                margin: 0,
              }}
            >
              You have successfully approved the contract to spend your cFAR tokens. Now click &quot;Swap Tokens&quot; below to complete the swap.
            </p>
          </div>
        )}

        {/* Swap Enabled/Disabled Warning */}
        {!swapActive && (
          <div
            style={{
              backgroundColor: colors.brand.red.base + '20',
              padding: spacing.lg,
              borderRadius: borders.component.default.radius,
              border: `1px solid ${colors.brand.red.base}`,
              marginBottom: spacing.lg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                marginBottom: spacing.sm,
              }}
            >
              <AlertCircle size={20} color={colors.brand.red.base} />
              <span
                style={{
                  fontSize: typography.fontSize.base,
                  color: colors.text.primary.default,
                  fontWeight: typography.fontWeight.semibold,
                }}
              >
                Swapping is Disabled
              </span>
            </div>
            <p
              style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary.default,
                margin: 0,
              }}
            >
              Swapping is currently disabled by the contract administrator. Please try again later or contact support.
            </p>
          </div>
        )}

        {/* Swap Amount Input */}
        <div
          style={{
            marginBottom: spacing.lg,
          }}
        >
          <label
            style={{
              display: 'block',
              fontSize: typography.fontSize.sm,
              color: colors.text.secondary.default,
              marginBottom: spacing.sm,
              fontWeight: typography.fontWeight.medium,
            }}
          >
            Amount to Swap (cFAR)
          </label>
          <div
            style={{
              position: 'relative',
            }}
          >
            <input
              type="text"
              value={swapAmount}
              onChange={(e) => setSwapAmount(e.target.value)}
              placeholder="0.00"
              style={{
                width: '100%',
                padding: spacing.lg,
                fontSize: typography.fontSize.xl,
                fontWeight: typography.fontWeight.semibold,
                backgroundColor: colors.background.secondary.default,
                color: colors.text.primary.default,
                border: `2px solid ${colors.border.default.default}`,
                borderRadius: borders.component.default.radius,
                fontFamily: typography.fontFamily.sans,
              }}
            />
            <button
              onClick={() => setSwapAmount(formatEther(oldTokenBalance || 0n))}
              disabled={!oldTokenBalance || oldTokenBalance === 0n}
              onMouseEnter={() => setIsMaxHovered(true)}
              onMouseLeave={() => setIsMaxHovered(false)}
              style={{
                position: 'absolute',
                right: spacing.md,
                top: '50%',
                transform:
                  oldTokenBalance && oldTokenBalance > 0n && isMaxHovered
                    ? 'translateY(-50%) translateY(-2px) scale(1.05)'
                    : 'translateY(-50%)',
                padding: `${spacing.xs} ${spacing.md}`,
                fontSize: typography.fontSize.sm,
                fontWeight: typography.fontWeight.medium,
                backgroundColor: colors.brand.purple.base,
                color: colors.neutral.white,
                border: 'none',
                borderRadius: borders.component.button.radius,
                cursor: oldTokenBalance && oldTokenBalance > 0n ? 'pointer' : 'not-allowed',
                opacity: oldTokenBalance && oldTokenBalance > 0n ? 1 : 0.5,
                boxShadow:
                  oldTokenBalance && oldTokenBalance > 0n && isMaxHovered
                    ? `${shadows.interactive.purpleHover}, ${shadows.elevation.medium}`
                    : shadows.interactive.purple,
                transition: transitions.button,
                backdropFilter: 'blur(8px)',
              }}
            >
              MAX
            </button>
          </div>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              marginTop: spacing.sm,
              fontSize: typography.fontSize.sm,
              color: colors.text.muted.default,
            }}
          >
            <span>Your balance: {formatTokenAmount(oldTokenBalance)} cFAR</span>
            <span>Pool available: {formatTokenAmount(availablePoolTokens)} nFAR</span>
          </div>
        </div>

        {/* Swap Preview */}
        {swapAmount && swapAmountBigInt > 0n && (
          <div
            style={{
              backgroundColor: colors.background.secondary.default,
              padding: spacing.lg,
              borderRadius: borders.component.default.radius,
              marginBottom: spacing.lg,
            }}
          >
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                marginBottom: spacing.md,
              }}
            >
              <span
                style={{
                  fontSize: typography.fontSize.base,
                  color: colors.text.secondary.default,
                }}
              >
                You will swap
              </span>
              <span
                style={{
                  fontSize: typography.fontSize.xl,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.text.primary.default,
                }}
              >
                {swapAmount} cFAR
              </span>
            </div>

            <div
              style={{
                textAlign: 'center',
                padding: `${spacing.sm} 0`,
              }}
            >
              <ArrowRight size={24} color={colors.brand.purple.base} style={{ transform: 'rotate(90deg)' }} />
            </div>

            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
              }}
            >
              <span
                style={{
                  fontSize: typography.fontSize.base,
                  color: colors.text.secondary.default,
                }}
              >
                You will receive
              </span>
              <span
                style={{
                  fontSize: typography.fontSize.xl,
                  fontWeight: typography.fontWeight.bold,
                  color: colors.brand.purple.base,
                }}
              >
                {swapAmount} nFAR
              </span>
            </div>
          </div>
        )}

        {/* Swap Blockers */}
        {swapBlockers.length > 0 && swapAmount && swapAmountBigInt > 0n && (
          <div
            style={{
              backgroundColor: colors.brand.yellow.base + '20',
              padding: spacing.lg,
              borderRadius: borders.component.default.radius,
              border: `1px solid ${colors.brand.yellow.base}`,
              marginBottom: spacing.lg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                marginBottom: spacing.sm,
              }}
            >
              <AlertCircle size={20} color={colors.brand.yellow.base} />
              <span
                style={{
                  fontSize: typography.fontSize.base,
                  color: colors.text.primary.default,
                  fontWeight: typography.fontWeight.semibold,
                }}
              >
                {needsApproval ? 'Approval Required' : 'Cannot Swap'}
              </span>
            </div>
            <ul
              style={{
                margin: 0,
                paddingLeft: spacing.lg,
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary.default,
              }}
            >
              {swapBlockers.map((blocker, index) => (
                <li key={index}>{blocker}</li>
              ))}
            </ul>
          </div>
        )}

        {/* Approve Button */}
        {needsApproval && swapAmount && swapAmountBigInt > 0n && (
          <button
            onClick={onApprove}
            disabled={
              chainId !== requiredChainId ||
              swapStep === 'approving' ||
              isApprovePending ||
              isApproveLoading ||
              !swapActive
            }
            onMouseEnter={() => setIsApproveHovered(true)}
            onMouseLeave={() => setIsApproveHovered(false)}
            style={{
              width: '100%',
              padding: spacing.lg,
              backgroundColor:
                chainId !== requiredChainId ||
                swapStep === 'approving' ||
                isApprovePending ||
                isApproveLoading ||
                !swapActive
                  ? colors.background.secondary.default
                  : colors.brand.yellow.base,
              color:
                chainId !== requiredChainId ||
                swapStep === 'approving' ||
                isApprovePending ||
                isApproveLoading ||
                !swapActive
                  ? colors.text.muted.default
                  : colors.neutral.black,
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.semibold,
              borderRadius: borders.component.button.radius,
              border: 'none',
              cursor:
                chainId !== requiredChainId ||
                swapStep === 'approving' ||
                isApprovePending ||
                isApproveLoading ||
                !swapActive
                  ? 'not-allowed'
                  : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
              marginBottom: spacing.md,
              boxShadow:
                chainId === requiredChainId &&
                swapStep === 'idle' &&
                !isApprovePending &&
                !isApproveLoading &&
                swapActive &&
                isApproveHovered
                  ? `${shadows.glow.orange.lg}, ${shadows.elevation.medium}`
                  : chainId === requiredChainId &&
                    swapStep === 'idle' &&
                    !isApprovePending &&
                    !isApproveLoading &&
                    swapActive
                  ? shadows.elevation.low
                  : 'none',
              transform:
                chainId === requiredChainId &&
                swapStep === 'idle' &&
                !isApprovePending &&
                !isApproveLoading &&
                swapActive &&
                isApproveHovered
                  ? 'translateY(-2px) scale(1.02)'
                  : 'translateY(0) scale(1)',
              transition: transitions.button,
              backdropFilter: 'blur(8px)',
            }}
          >
            {(swapStep === 'approving' || isApprovePending || isApproveLoading) && (
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            )}
            {swapStep === 'approving' || isApprovePending
              ? 'Waiting for approval...'
              : isApproveLoading
              ? 'Confirming approval...'
              : 'Approve cFAR'}
          </button>
        )}

        {/* Swap Button */}
        {!needsApproval && swapAmount && swapAmountBigInt > 0n && (
          <button
            onClick={onSwap}
            disabled={!canSwap || swapStep === 'swapping' || isSwapPending || isSwapLoading}
            onMouseEnter={() => setIsSwapHovered(true)}
            onMouseLeave={() => setIsSwapHovered(false)}
            style={{
              width: '100%',
              padding: spacing.lg,
              backgroundColor:
                !canSwap || swapStep === 'swapping' || isSwapPending || isSwapLoading
                  ? colors.background.secondary.default
                  : colors.brand.purple.base,
              color:
                !canSwap || swapStep === 'swapping' || isSwapPending || isSwapLoading
                  ? colors.text.muted.default
                  : colors.neutral.white,
              fontSize: typography.fontSize.lg,
              fontWeight: typography.fontWeight.semibold,
              borderRadius: borders.component.button.radius,
              border: 'none',
              cursor:
                !canSwap || swapStep === 'swapping' || isSwapPending || isSwapLoading ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: spacing.sm,
              boxShadow:
                canSwap && swapStep === 'idle' && !isSwapPending && !isSwapLoading && isSwapHovered
                  ? `${shadows.interactive.purpleHover}, ${shadows.elevation.medium}`
                  : canSwap && swapStep === 'idle' && !isSwapPending && !isSwapLoading
                  ? shadows.interactive.purple
                  : 'none',
              transform:
                canSwap && swapStep === 'idle' && !isSwapPending && !isSwapLoading && isSwapHovered
                  ? 'translateY(-2px) scale(1.02)'
                  : 'translateY(0) scale(1)',
              transition: transitions.button,
              backdropFilter: 'blur(8px)',
            }}
          >
            {(swapStep === 'swapping' || isSwapPending || isSwapLoading) && (
              <Loader2 size={20} style={{ animation: 'spin 1s linear infinite' }} />
            )}
            {swapStep === 'swapping' || isSwapPending
              ? 'Waiting for swap...'
              : isSwapLoading
              ? 'Confirming swap...'
              : 'Swap Tokens'}
          </button>
        )}

        {/* Transaction Error */}
        {(isApproveError || isSwapError) && (
          <div
            style={{
              backgroundColor: colors.brand.yellow.base + '20',
              padding: spacing.lg,
              borderRadius: borders.component.default.radius,
              border: `1px solid ${colors.brand.yellow.base}`,
              marginTop: spacing.lg,
            }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: spacing.sm,
                marginBottom: spacing.sm,
              }}
            >
              <AlertCircle size={24} color={colors.brand.yellow.dark} />
              <span
                style={{
                  fontSize: typography.fontSize.lg,
                  color: colors.text.primary.default,
                  fontWeight: typography.fontWeight.semibold,
                }}
              >
                Transaction Cancelled
              </span>
            </div>
            <p
              style={{
                fontSize: typography.fontSize.sm,
                color: colors.text.secondary.default,
                margin: 0,
              }}
            >
              {formatErrorMessage(approveError || swapError)}
            </p>
          </div>
        )}
      </Card>
    </>
  );
};
